import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Spinner, Alert } from 'reactstrap';
import { FiBell } from 'react-icons/fi';
import alertsService from '../../services/alerts';
import teamService from '../../services/team';
import firebase from '../../config/firebase.config';
import { useTeamMemberDetailsForSelect } from '../../hooks/useTeamMemberDetailsForSelect';
import { useActiveCompanyRole } from '../../hooks/usePermission';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { queryKeys } from '../../config/queryKeys';
import './styles.css';
import styles from './Alerts.module.scss';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { alertsTableConfig } from './Alerts.config';
import { manageTeamMembersActionTypes } from '@/store/ManageTeams/ManageTeams.actionTypes';
import { Table as AntdTable } from 'antd';
import { EmptyState } from '@/components/EmptyState';
import {
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlFilterWrite,
} from '@/hooks/useUrlFilterSync';
const database = firebase.firestore();
const allTeamsOption = {
  value: 'all-departments',
  label: 'All Departments',
  isAllTeams: true,
};
const allEmployeesOption = {
  value: 'all-employees',
  label: 'All Employees',
  isAllEmployees: true,
};
// Stable (module-level) so they never churn `useUrlMemberHydrationSync`'s
// `getDefaultMember` dependency across renders.
const getDefaultTeam = () => allTeamsOption;
const getDefaultEmployee = () => allEmployeesOption;

export const AlertsComponent = () => {
  const dispatch = useDispatch();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states — start unresolved; URL-hydration below resolves each to
  // either a deep-linked id or its "all" default once its options list loads.
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const location = useLocation();
  const history = useHistory();

  // Options for dropdowns
  const [teamOptions, setTeamOptions] = useState([]);
  const teamOptionsRef = useRef(teamOptions);
  teamOptionsRef.current = teamOptions;

  // Cache for team and employee names
  const [teamNameMap, setTeamNameMap] = useState({});
  const [employeeNameMap, setEmployeeNameMap] = useState({});
  const [enrichedAlerts, setEnrichedAlerts] = useState([]);
  const [allMemberDetailsObject, setAllMemberDetailsObject] = useState({});

  const authState = useSelector((state) => ({ ...state.auth }));
  const userId = authState.profile?.id;
  const companyId = authState.profile?.activeCompany?.companyId;
  const activeCompanyRole = useActiveCompanyRole();
  const queryClient = useQueryClient();

  const teamId = selectedTeam?.value ?? null;
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
    viewerSeesOnlySelf,
  } = useTeamMemberDetailsForSelect(selectedTeam?.isAllTeams ? null : teamId);
  const {
    allTeams,
    allMembers,
    failure,
    loading: allMembersLoading,
  } = useSelector((state) => {
    return {
      allTeams: state.team.teams?.data,
      allMembers: state.manageTeams?.members?.successPayload?.data,
      failure: state.manageTeams?.members?.failure,
      loading: state.manageTeams?.members?.pending,
    };
  });
  const getAllTeamMembersData = () => {
    dispatch({
      type: manageTeamMembersActionTypes.GET_MEMBERS,
      payload: {
        teams: allTeams,
      },
    });
  };

  useEffect(() => {
    if (selectedTeam?.isAllTeams) {
      if (!allMembers?.length) {
        getAllTeamMembersData();
      }
    }
  }, [selectedTeam]);
  useEffect(() => {
    if (allMembers?.length) {
      const allMemberDetailsObject = {};
      allMembers.forEach((member) => {
        allMemberDetailsObject[member.userUID] = {
          ...member,
          userName: member.userName,
          userEmailid: member.userEmailid,
          userType: member.userType,
        };
      });
      setAllMemberDetailsObject(allMemberDetailsObject);
    }
  }, [allMembers]);
  const employeeOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    let memberDetailsObject = memberDetails;
    if (selectedTeam?.isAllTeams) {
      memberDetailsObject = allMemberDetailsObject;
    }
    const employeesOption = Object.entries(memberDetailsObject)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.userName || m.userEmailid || uid,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    if (viewerSeesOnlySelf) {
      return employeesOption;
    }
    return [allEmployeesOption, ...employeesOption];
  }, [memberDetails, viewerSeesOnlySelf, allMemberDetailsObject, selectedTeam]);

  const employeeOptionsRef = useRef(employeeOptions);
  employeeOptionsRef.current = employeeOptions;

  // ── URL-synced filters ────────────────────────────────────────────────────
  // Reuses the generic member-select sync (paramKey lets it double as a
  // "team" sync too) so refresh, browser back/forward, and links from other
  // pages (e.g. a dashboard's "View all alerts") all restore the same
  // department/employee filter instead of resetting to "All".
  useUrlMemberHydrationSync({
    location,
    membersOptions: teamOptions,
    selectedMember: selectedTeam,
    setSelectedMember: setSelectedTeam,
    getDefaultMember: getDefaultTeam,
    paramKey: 'team',
  });
  useUrlMemberNavSync({
    location,
    membersOptionsRef: teamOptionsRef,
    setSelectedMember: setSelectedTeam,
    paramKey: 'team',
  });

  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(selectedTeam),
    membersOptions: employeeOptions,
    selectedMember: selectedEmployee,
    setSelectedMember: setSelectedEmployee,
    getDefaultMember: getDefaultEmployee,
    paramKey: 'member',
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(selectedTeam),
    membersOptionsRef: employeeOptionsRef,
    setSelectedMember: setSelectedEmployee,
    paramKey: 'member',
  });

  // Omit a key entirely while still unresolved (null — nothing loaded yet),
  // but once resolved, write '' to actively clear it when the selection is
  // the "All" sentinel — otherwise switching back to "All" would leave a
  // stale team/member id sitting in the URL (mergeSearchParams treats an
  // omitted key as "leave as-is", not "clear").
  const urlWriteParams = useMemo(() => {
    const params = {};
    if (selectedTeam) {
      params.team = selectedTeam.isAllTeams ? '' : selectedTeam.value;
    }
    if (selectedEmployee) {
      params.member = selectedEmployee.isAllEmployees
        ? ''
        : selectedEmployee.value;
    }
    return params;
  }, [selectedTeam, selectedEmployee]);

  useUrlFilterWrite({
    history,
    location,
    params: urlWriteParams,
  });

  // Fetch teams list on initial load
  useEffect(() => {
    const fetchTeams = async () => {
      if (!companyId || !userId) return;

      try {
        const teamsResponse = await teamService.getMemberTeamCollection({
          companyId,
          userId,
          role: activeCompanyRole,
          companyRole: activeCompanyRole,
        });

        if (teamsResponse.success) {
          const options = teamsResponse.data.map((team) => ({
            value: team.docId,
            label: team.Team,
          }));
          setTeamOptions([allTeamsOption, ...options]);
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
      }
    };

    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId]);

  // Reset employee when team changes (no selected team = no selected employee)
  useEffect(() => {
    if (!selectedTeam) {
      setSelectedEmployee(null);
    }
  }, [selectedTeam]);

  // Team Members only see themselves — clear "All employees" or another user
  useEffect(() => {
    if (!viewerSeesOnlySelf || !userId) return;
    setSelectedEmployee((prev) => {
      if (!prev) return prev;
      if (prev.isAllEmployees || prev.value !== userId) {
        const label =
          memberDetails?.[userId]?.userName ||
          memberDetails?.[userId]?.userEmailid ||
          userId;
        return { value: userId, label };
      }
      return prev;
    });
  }, [viewerSeesOnlySelf, userId, memberDetails]);

  // Fetch team names and employee names for alerts
  useEffect(() => {
    const enrichAlerts = async () => {
      if (alerts.length === 0) {
        setEnrichedAlerts([]);
        return;
      }

      const teamNameMapLocal = { ...teamNameMap };
      const employeeNameMapLocal = { ...employeeNameMap };

      // Get unique teamIds and userIds from alerts
      const uniqueTeamIds = [
        ...new Set(alerts.map((a) => a.teamId).filter(Boolean)),
      ];
      const uniqueUserIds = [
        ...new Set(alerts.map((a) => a.userId).filter(Boolean)),
      ];

      // Fetch team names
      const teamPromises = uniqueTeamIds
        .filter((teamId) => !teamNameMapLocal[teamId])
        .map(async (teamId) => {
          try {
            const teamResponse = await teamService.getTeamData(teamId);
            if (teamResponse.success && teamResponse.data) {
              return {
                teamId,
                teamName:
                  teamResponse.data.teamName ||
                  teamResponse.data.Team ||
                  'Unknown Department',
              };
            }
            return { teamId, teamName: 'Unknown Department' };
          } catch (err) {
            console.error(`Error fetching team ${teamId}:`, err);
            return { teamId, teamName: 'Unknown Department' };
          }
        });

      const teamResults = await Promise.all(teamPromises);
      teamResults.forEach(({ teamId, teamName }) => {
        teamNameMapLocal[teamId] = teamName;
      });

      // Fetch employee names via shared cache (queryClient.fetchQuery)
      const memberDetailsByTeam = {};
      await Promise.all(
        uniqueTeamIds.map(async (tid) => {
          try {
            const details = await queryClient.fetchQuery({
              queryKey: queryKeys.teamMemberDetails.detail(tid),
              queryFn: () => getTeamMemberDetails(database, tid),
            });
            memberDetailsByTeam[tid] = details;
          } catch (err) {
            console.error(`Error fetching members for team ${tid}:`, err);
            memberDetailsByTeam[tid] = {};
          }
        })
      );

      alerts.forEach((alert) => {
        if (!alert.teamId || !alert.userId) return;
        const key = `${alert.teamId}_${alert.userId}`;
        if (employeeNameMapLocal[key]) return;
        const member = memberDetailsByTeam[alert.teamId]?.[alert.userId];
        employeeNameMapLocal[key] =
          member?.userName ||
          member?.userEmailid ||
          alert.metadata?.userEmailId ||
          'Unknown Employee';
      });

      setTeamNameMap(teamNameMapLocal);
      setEmployeeNameMap(employeeNameMapLocal);

      // Enrich alerts with display data
      const enriched = alerts.map((alert) => ({
        ...alert,
        employeeName:
          employeeNameMapLocal[`${alert.teamId}_${alert.userId}`] ||
          alert.metadata?.userEmailId ||
          'Unknown Employee',
        teamName: teamNameMapLocal[alert.teamId] || 'Unknown Department',
        currentProductivity: alert.metadata?.productivityPercentage || 0,
        expectedProductivity: alert.metadata?.threshold || 0,
      }));

      setEnrichedAlerts(enriched);
    };

    enrichAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  // Filter alerts based on selected team and employee

  const filteredAlerts = useMemo(() => {
    return enrichedAlerts.filter((alert) => {
      if (
        !selectedTeam?.isAllTeams &&
        selectedTeam &&
        alert.teamId !== selectedTeam.value
      ) {
        return false;
      }
      if (selectedEmployee?.isAllEmployees) {
        return true;
      }
      if (selectedEmployee && alert.userId !== selectedEmployee.value) {
        return false;
      }
      return true;
    });
  }, [enrichedAlerts, selectedTeam, selectedEmployee]);
  const groupedAlerts = useMemo(() => {
    return filteredAlerts.reduce((acc, alert) => {
      if (!acc[alert.userId]) {
        acc[alert.userId] = {
          ...alert,
          key: 'group-' + alert.userId,
          userId: alert.userId,
          employeeName: alert.employeeName,
          teamName: alert.teamName,
          children: [],
        };
      }
      acc[alert.userId].children.push({
        key: alert.id,
        ...alert,
      });
      return acc;
    }, {});
  });
  // Fetch alerts and auto-mark as read when page opens
  useEffect(() => {
    console.log('🔔 AlertsPage: useEffect triggered', { userId });

    const markAlertsOnLoad = async () => {
      console.log('🔔 AlertsPage: markAlertsOnLoad started', { userId });

      if (!userId) {
        console.log('🔔 AlertsPage: No userId, returning early');
        return;
      }

      try {
        console.log('🔔 AlertsPage: Fetching alerts...', { userId });

        // First fetch alerts
        const options = {
          includeRead: true,
          limit: 100,
          orderBy: 'createdAt',
          orderDirection: 'desc',
        };

        const response = await alertsService.getUserAlerts(userId, options);
        console.log('🔔 AlertsPage: getUserAlerts response', {
          success: response.success,
          totalAlerts: response.data?.length,
          response,
        });

        if (response.success) {
          const fetchedAlerts = response.data;
          console.log('🔔 AlertsPage: Fetched alerts', {
            totalCount: fetchedAlerts.length,
            alerts: fetchedAlerts.map((a) => ({
              id: a.id,
              title: a.title,
              isRead: a.isRead,
              teamId: a.teamId,
              userId: a.userId,
            })),
          });

          setAlerts(fetchedAlerts);

          // Check if there are unread alerts and mark them as read
          const unreadAlertIds = fetchedAlerts
            .filter((alert) => !alert.isRead)
            .map((alert) => alert.id);

          console.log('🔔 AlertsPage: Unread alerts check', {
            totalAlerts: fetchedAlerts.length,
            unreadCount: unreadAlertIds.length,
            unreadAlertIds,
          });

          if (unreadAlertIds.length > 0) {
            console.log('🔔 AlertsPage: Marking alerts as read...', {
              unreadAlertIds,
              userId,
            });

            const markResponse =
              await alertsService.markAllAlertsAsRead(userId);

            console.log('🔔 AlertsPage: markAlertsAsRead response', {
              success: markResponse.success,
              message: markResponse.message,
              error: markResponse.error,
            });

            if (markResponse.success) {
              console.log(
                '🔔 AlertsPage: Successfully marked alerts as read, updating local state'
              );

              // Update local state to reflect read status
              setAlerts((prevAlerts) => {
                const updated = prevAlerts.map((alert) => {
                  if (unreadAlertIds.includes(alert.id)) {
                    return { ...alert, isRead: true };
                  }
                  return alert;
                });
                return updated;
              });
            } else {
              console.error(
                '🔔 AlertsPage: Failed to mark alerts as read',
                markResponse
              );
            }
          } else {
            console.log('🔔 AlertsPage: No unread alerts to mark');
          }
        } else {
          console.error('🔔 AlertsPage: Failed to fetch alerts', response);
          setError(response.error || 'Failed to fetch alerts');
        }
      } catch (err) {
        console.error('🔔 AlertsPage: Error fetching/marking alerts', err);
        setError('An error occurred while fetching alerts');
      } finally {
        console.log('🔔 AlertsPage: Setting loading to false');
        setLoading(false);
      }
    };

    if (userId) {
      console.log('🔔 AlertsPage: userId available, starting markAlertsOnLoad');
      setLoading(true);
      markAlertsOnLoad();
    } else {
      console.log('🔔 AlertsPage: No userId available, skipping');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <>
      <div className={styles.alertsPage}>
        <div className={styles.alertsPageContainer}>
          <Text
            variant={textVariantsEnum.Header}
            fontWeight={fontWeightEnum.Normal}
          >
            <FiBell className="mr-2" />
            Productivity Alert
          </Text>
          <div className={styles.filtersContainer}>
            <div className={styles.dropdown}>
              <Dropdown
                label="Department"
                selectStyles={borderedSelectStyles}
                options={teamOptions}
                value={selectedTeam}
                onChange={(opt) => {
                  if (opt.isAllTeams) {
                    setSelectedEmployee(allEmployeesOption);
                  }
                  setSelectedTeam(opt);
                }}
              />
            </div>
            <div className={styles.dropdown}>
              <Dropdown
                label={`${membersLoading || allMembersLoading ? 'Loading...' : 'Select Employee'}`}
                selectStyles={borderedSelectStyles}
                options={employeeOptions}
                value={selectedEmployee}
                onChange={(opt) => setSelectedEmployee(opt)}
              />
              {membersError && selectedTeam && (
                <div className="mb-1 text-danger small">
                  Failed to load members. {membersErrorObj?.message || ''}{' '}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => refetchMembers()}
                  >
                    Retry
                  </Button>
                </div>
              )}
            </div>
          </div>
          {error && (
            <Alert color="danger" className="mt-3 w-100">
              <strong>Error:</strong> {error}
              <Button
                color="danger"
                size="sm"
                className="ml-2"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </Alert>
          )}
          {/* {markingAsRead && (
            <Alert color="info" className="mt-3 w-100">
              <Spinner size="sm" className="mr-2" />
              Marking alerts as read...
            </Alert>
          )} */}
          <div className={styles.alertsTableContainer}>
            {loading && (
              <div className="text-center py-4">
                <Spinner color="primary" />
                <p className="mt-2">Loading alerts...</p>
              </div>
            )}
            {!loading && filteredAlerts.length === 0 && (
              <EmptyState
                illustration
                title="No Alerts Found"
                description={
                  selectedTeam || selectedEmployee
                    ? 'No alerts match the selected filters.'
                    : 'You have no alerts at this time.'
                }
              />
            )}
            {!loading && filteredAlerts.length > 0 && (
              <AntdTable
                rootClassName={styles.alertsAntdTable}
                dataSource={Object.values(groupedAlerts)}
                pagination={false}
                columns={alertsTableConfig()}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
